import { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LastWeekReportCard from '../components/LastWeekReportCard';
import { countdownForDisplay, countdownMessage } from '../utils/mcsDisplay';
import { isReminderUnset } from '../utils/reminderSetup';
import { isPreviousResult } from '../utils/summaryProgress';
import { useSummaryWait } from '../hooks/useSummaryWait';
import {
  employeeApi,
  voiceRecordsApi,
  notificationSettingsApi,
  RequestError,
  type DailyReport,
  type DailyReportDomains,
  type DailyReportCalm,
  type DailyReportDayOverDay,
  type DailyReportColdStart,
  type DailyReportPatternANotification,
  type MCSResponse,
  type VoiceRecord,
  type DailyConditionResponse,
} from '../api/client';

/**
 * v1.1 DCS の 4 バンド表示（condition_display バッジ）の表示可否フラグ。
 *
 * BE の数学的診断（2026-08）により、4 バンドは 28 日窓では good(<0.250)/tired(≥0.420) に
 * 構造的に到達不能で、実データ上は常に fair（tone=blue）に固定されることが確定した
 * （係数スケールと集計窓の不整合。仕様書 §3c でも 0.250/0.420 は「暫定値」。確定は二値 p≥0.334 のみ）。
 * 常時同一バッジはユーザーをミスリードするため、バンド設計が校正されるまで非表示にする。
 * 校正が完了したら true に戻す。疲労度ゾーンは引き続き表示する
 * （月次 MCS の二値 risk_flag の「高リスク／低リスク」は、課題 #101-9 により従業員の画面には出さない）。
 */
const SHOW_CONDITION_BAND = false;

/**
 * リマインド未設定の案内を閉じたことを覚えておくキー（課題 #78）。
 * 毎回出し続けると通知そのものが疎まれるため、一度閉じたら再表示しない。
 */
const REMINDER_PROMPT_DISMISSED_KEY = 'ceeu.reminderPromptDismissed';

function readReminderPromptDismissed(): boolean {
  try {
    return localStorage.getItem(REMINDER_PROMPT_DISMISSED_KEY) === '1';
  } catch {
    return false; // プライベートモード等で参照できない場合は案内を出す
  }
}

function errorMessage(status: number, t: (key: string) => string): string {
  if (status === 401) return t('dashboard.error401');
  if (status === 404) return t('dashboard.error404');
  if (status >= 500) return t('dashboard.error500');
  return t('dashboard.errorLoad');
}

/** day_over_day.direction を仕様書 §10.1 のラベルに変換 */
function directionLabel(d: DailyReportDayOverDay | undefined, t: (key: string) => string): string {
  if (!d || d.direction == null) return t('dashboard.dayOverDay.unknown');
  switch (d.direction) {
    case 'up':
      return t('dashboard.dayOverDay.up');
    case 'down':
      return t('dashboard.dayOverDay.down');
    case 'flat':
      return t('dashboard.dayOverDay.flat');
    default:
      return t('dashboard.dayOverDay.unknown');
  }
}

export default function EmployeeDashboard() {
  const { t } = useTranslation();
  const { getToken, clearSession, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state as { message?: string; justSubmittedVoice?: boolean } | null;
  const successMessage = locationState?.message;
  const justSubmittedVoice = locationState?.justSubmittedVoice;
  const [report, setReport] = useState<DailyReport | null>(null);
  /** 次にまとめを作り始める時刻の目安（#174） */
  const [nextGenerationAt, setNextGenerationAt] = useState<string | null>(null);
  const [mcs, setMcs] = useState<MCSResponse | null>(null);
  const [todayRecords, setTodayRecords] = useState<VoiceRecord[]>([]);
  /**
   * デイリーコンディション（v1.1 DCS / 2026-07-02）
   * 取得失敗時 (BE 未デプロイ・404 等) は null のまま既存 UI に自然フォールバック
   */
  const [condition, setCondition] = useState<DailyConditionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [reportError, setReportError] = useState<string | null>(null);
  const [mcsError, setMcsError] = useState<string | null>(null);
  /**
   * 当日音声記録の取得失敗（BE test_p0_api(c) 既知の 400 含む）。
   * 旧実装は catch で空配列にフォールバックしてエラーを隠していたが、
   * ユーザーが「録音したのに記録が見えない」状況に気づけないため可視化する
   */
  const [recordsError, setRecordsError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  /** リマインド未設定の案内（課題 #78）。取得失敗時は何も出さない */
  const [reminderUnset, setReminderUnset] = useState(false);
  const [reminderPromptDismissed, setReminderPromptDismissed] = useState(readReminderPromptDismissed);

  // daily_reports は別の「レポート生成」処理（バッチ等）で挿入される。未実行だと当日分は 0 件。
  // その間は GET /v1/voice-records で今日の音声記録を取得し、フォールバック表示に利用する
  const loadData = useCallback((background = false) => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return () => {};
    }
    // まとめを追いかける間の読み直しでは、画面を「読み込み中」に戻さない。
    // 戻すと 15 秒ごとに画面が消え、前回の結果も、待ち時間の測り始めも消えてしまう
    if (!background) {
      setReportError(null);
      setMcsError(null);
      setRecordsError(null);
      setLoading(true);
    }
    let cancelled = false;
    const now = new Date();
    const y = now.getFullYear(), m = now.getMonth(), d = now.getDate();
    const startOfDayLocal = new Date(y, m, d, 0, 0, 0, 0);
    const endOfDayLocal = new Date(y, m, d, 23, 59, 59, 999);
    const fromUTC = startOfDayLocal.toISOString();
    const toUTC = endOfDayLocal.toISOString();
    // 当日日付を YYYY-MM-DD (JST 想定) で構築
    const dateYmd = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const userIdForCondition = user?.userId ?? '';
    Promise.all([
      employeeApi.getTodayReport(token).catch((e) => e as Error),
      employeeApi.getMcs(token).catch((e) => e as Error),
      voiceRecordsApi.getList(token, { from: fromUTC, to: toUTC, limit: 10 }).catch((e) => e as Error),
      // v1.1 DCS: 失敗時は null で継続（既存 UI にフォールバック）
      userIdForCondition
        ? employeeApi.getDailyCondition(userIdForCondition, dateYmd, token).catch(() => null)
        : Promise.resolve(null),
    ]).then(async ([reportRes, mcsRes, recordsRes, conditionRes]) => {
      if (cancelled) return;
      const got401 =
        (reportRes instanceof RequestError && reportRes.status === 401) ||
        (mcsRes instanceof RequestError && mcsRes.status === 401);
      if (got401) {
        clearSession();
        navigate('/login', { replace: true, state: { sessionExpired: true } });
        return;
      }
      if (reportRes instanceof Error) {
        setReport(null);
        setNextGenerationAt(null);
        const msg = reportRes instanceof RequestError ? errorMessage(reportRes.status, t) : t('dashboard.errorLoad');
        setReportError(msg);
      } else {
        setReport(reportRes.report);
        setNextGenerationAt(reportRes.nextGenerationAt);
        setReportError(null);
      }
      if (mcsRes instanceof Error) {
        setMcs(null);
        const msg = mcsRes instanceof RequestError ? errorMessage(mcsRes.status, t) : t('dashboard.errorLoad');
        setMcsError(msg);
      } else {
        setMcs(mcsRes);
        setMcsError(null);
      }
      if (recordsRes instanceof Error) {
        // 401 は他 API と同じくクリア（理論上 reportRes/mcsRes で先に検知される）
        if (recordsRes instanceof RequestError && recordsRes.status === 401) {
          clearSession();
          navigate('/login', { replace: true, state: { sessionExpired: true } });
          return;
        }
        setTodayRecords([]);
        const msg =
          recordsRes instanceof RequestError
            ? t('dashboard.errorRecords', { status: recordsRes.status })
            : t('dashboard.errorRecordsNetwork');
        setRecordsError(msg);
        // 開発時のデバッグログ（本番ではコンソールから消えても無害）
        console.warn('[voice-records] fetch failed:', recordsRes);
      } else {
        const raw = (recordsRes as unknown as { data?: VoiceRecord[]; items?: VoiceRecord[] }) ?? null;
        const list = raw
          ? (Array.isArray(raw.data) ? raw.data : Array.isArray(raw.items) ? raw.items : [])
          : [];
        setTodayRecords(list);
        setRecordsError(null);
      }
      // v1.1 DCS: BE 未実装/未デプロイ時は null が返り、静かに既存 UI にフォールバック
      setCondition(conditionRes ?? null);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [getToken, clearSession, navigate, t, user?.userId]);

  useEffect(() => {
    const cleanup = loadData();
    return () => {
      if (typeof cleanup === 'function') cleanup();
    };
  }, [loadData, retryKey]);

  /**
   * リマインド設定の有無だけを確認する（課題 #78）。
   * ダッシュボードの描画は待たせず、失敗しても黙って何も出さない。
   */
  useEffect(() => {
    if (reminderPromptDismissed) return;
    const token = getToken();
    if (!token) return;
    let alive = true;
    notificationSettingsApi
      .get(token)
      .then((data) => {
        if (alive) setReminderUnset(isReminderUnset(data));
      })
      .catch(() => {
        /* 設定が取れないときは案内しない */
      });
    return () => {
      alive = false;
    };
  }, [getToken, reminderPromptDismissed]);

  const dismissReminderPrompt = useCallback(() => {
    setReminderPromptDismissed(true);
    try {
      localStorage.setItem(REMINDER_PROMPT_DISMISSED_KEY, '1');
    } catch {
      /* 保存できなくても表示は閉じる */
    }
  }, []);

  // --- 表示用の派生値 ---
  const isNew = report?.schema === 'new';
  const isLegacy = report?.schema === 'legacy';
  const domains: DailyReportDomains | undefined = isNew ? report?.domains : undefined;
  const calm: DailyReportCalm | undefined = isNew ? report?.calm : undefined;
  const dayOverDay: DailyReportDayOverDay | undefined = isNew ? report?.dayOverDay : undefined;
  const coldStart: DailyReportColdStart | undefined = isNew ? report?.coldStart : undefined;
  const patternA: DailyReportPatternANotification | null | undefined =
    isNew ? report?.patternANotification : undefined;
  const showDomains = coldStart?.show_4_domains ?? true;
  const showCalm = coldStart?.show_calm ?? true;
  const showDayOverDay = coldStart?.show_day_over_day ?? true;
  // 仕様改訂（2026-04-26）: 月次ワーカーが日次実行になり当月途中でも short_reference が返る。
  // FE は cold_start.show_monthly_mcs に頼らず、常に MCS セクションを描画し
  // mode_applied で UI を分岐する

  // 旧スキーマ用
  const conditionEmoji = isLegacy ? (report?.condition_emoji ?? report?.condition ?? null) : null;
  const reportTrends = isLegacy && Array.isArray(report?.trends) ? report.trends : [];

  // 共通
  const hasTodayRecord = todayRecords.length > 0;

  /**
   * 当日の録音を「録音時刻の昇順」に並べ、1 件ずつ解析サマリを取り出す（課題 #42）。
   * 以前は todayRecords[0] のみを参照していたため、同日に複数回録音しても
   * 1 件目の解析結果しか表示されなかった。
   */
  const todayEntries = useMemo(() => {
    const at = (r: VoiceRecord) => r.recorded_at ?? r.created_at ?? '';
    return [...todayRecords]
      .sort((a, b) => at(a).localeCompare(at(b)))
      .map((r) => {
        const summary = r.analysis_json?.summary as Record<string, unknown> | undefined;
        const text =
          summary?.text != null && String(summary.text).trim() !== ''
            ? String(summary.text).trim()
            : null;
        // 録音ごとの解析（1〜2 秒）が済んでいるか。要約文が無くても済んでいることが
        // ある（無音の録音など）ので、要約文の有無とは分けて持つ
        const analyzed = r.analysis_json != null;
        return { id: r.id, recordedAt: at(r) || null, text, analyzed };
      });
  }, [todayRecords]);

  const hasTodaySummaryText = todayEntries.some((e) => e.text != null);

  /*
   * 「まとめを準備しています」の帯を出す条件（課題 #102 / #99）。
   *
   * 以前は「録音ごとの要約文が 1 件でも null なら処理中」と判定していた。
   * 無音の録音は要約文が永久に入らないため、まとめが表示されたあとも帯が
   * 残り続けた。録音ごとの解析は 1〜2 秒で終わっており、実際に待っているのは
   * 日次レポート（まとめ）の生成で、BE のワーカーが 5 分間隔で作る。
   * そこで、待つ対象をまとめに揃える。
   */
  const reportReady = Boolean(report?.comment) || domains != null;
  const reportSignature = reportReady ? JSON.stringify([report?.comment ?? null, domains ?? null]) : null;

  /*
   * まとめに入っている最後の録音の時刻（last_recorded_at）で、今回の録音が入ったかを見分ける（#174）。
   * 録音の一覧が読めないときと、last_recorded_at が無いまとめ（BE が入る前に作ったもの）は比べられない。
   */
  const canCompareRecording = Boolean(report?.lastRecordedAt) && !recordsError;
  const reportIsBehind = canCompareRecording && isPreviousResult(report?.lastRecordedAt, todayRecords);

  /*
   * 比べられないときだけ、送信直後に来た場合に、到着時点のまとめから内容が変わるまでを「準備中」とする。
   * 同じ日の 2 件目以降は、既にまとめがあっても新しい録音はまだ反映されていない。
   */
  const [signatureAtArrival, setSignatureAtArrival] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    if (justSubmittedVoice && !loading && signatureAtArrival === undefined) {
      setSignatureAtArrival(reportSignature);
    }
  }, [justSubmittedVoice, loading, signatureAtArrival, reportSignature]);
  // 到着時点のまとめを記録するまでの一瞬も「待機中」に含める。ここを準備完了と
  // 判定すると、後始末の処理が送信直後の印（location.state）を先に消してしまう
  const waitingForNewRecording =
    Boolean(justSubmittedVoice) &&
    !canCompareRecording &&
    (signatureAtArrival === undefined || reportSignature === signatureAtArrival);

  const preparingSummary = (hasTodayRecord && !reportReady) || reportIsBehind || waitingForNewRecording;
  /** 今出ているまとめは前回の結果で、今回の結果ではない（#174。隠さずに、分かる形で出す） */
  const showingPreviousResult = reportReady && (reportIsBehind || waitingForNewRecording);
  // ポーリングと location.state の後始末で使う
  const analysisReady = !preparingSummary;

  /*
   * まとめを追いかける条件（課題 #42 / #102 / #174）。
   * - 録音直後にダッシュボードへ来た場合
   * - まとめがまだ無い録音や、まとめにまだ入っていない録音がある場合（戻ってきたとき・リロード後も追いかける）
   */
  const showAnalyzing = preparingSummary && !loading;
  const reloadInBackground = useCallback(() => {
    loadData(true);
  }, [loadData]);
  const summaryWait = useSummaryWait(showAnalyzing, nextGenerationAt, reloadInBackground);

  // 送信直後フローの後始末: 解析が出揃ったら location.state を掃除する
  useEffect(() => {
    if (justSubmittedVoice && analysisReady) {
      navigate('.', { replace: true, state: { message: successMessage } });
    }
  }, [justSubmittedVoice, analysisReady, navigate, successMessage]);

  // 月次 MCS（仕様 0422 §6 / Phase B + 2026-04-26 改訂）。
  // mode_applied で UI 分岐:
  //   - standard: 正式 MCS スコア（バッジなし）
  //   - short_reference: スコア + 「（参考値）」バッジ
  //   - hold or available=false: 「MCS まであと N 日」カウントダウン
  const mcsAvailable = mcs?.available === true && mcs.mcs_score != null;
  const mcsScore = mcsAvailable ? mcs!.mcs_score! : null;
  const mcsMode = mcs?.mode_applied ?? null;
  const mcsCutoffVersion = mcs?.cutoff_version ?? null;
  const mcsYearMonth = mcs?.year_month ?? null;
  const mcsDataVolumePct = mcs?.data_volume_flag != null ? Math.round(mcs.data_volume_flag * 100) : null;
  const mcsIsReference = mcsMode === 'short_reference';
  const mcsShowScore = mcsAvailable && (mcsMode === 'standard' || mcsMode === 'short_reference');
  // 表示到達までの残量。#44 の確定により録音日数では判定しない（課題 #61）。
  // BE が segment 件数・音声件数を返すまでは残量を数値化できないため null になる。
  // 残り時間は日次レポートの値（直近 28 日）を使う。当日のレポートが無いときだけ月次の値（#61）
  const mcsCountdown = countdownMessage(countdownForDisplay(report, mcs));

  if (loading) {
    return (
      <div className="page employee-dashboard">
        <div className="dashboard-loading">{t('common.loading')}</div>
      </div>
    );
  }

  const clearSuccessMessage = () => navigate('.', { replace: true, state: {} });
  const previousBadge = showingPreviousResult && (
    <span className="previous-result-badge">{t('dashboard.summaryWait.previousBadge')}</span>
  );

  return (
    <div className="page employee-dashboard">
      <h1>{t('employee.dashboard')}</h1>

      {/* 使う順に合わせて、いちばん上に置く（#174。下にあると注意書きと重なることがあった） */}
      <p className="dashboard-record-action">
        <Link to="/record" className="dashboard-cta">
          {t('employee.recordVoice')}
        </Link>
      </p>

      {successMessage && (
        <div className="dashboard-success" role="status">
          {successMessage}
          <button type="button" className="dashboard-success-dismiss" onClick={clearSuccessMessage} aria-label={t('common.close')}>
            ×
          </button>
        </div>
      )}

      {/*
        課題 #78: 通知設定は「設定」の中の 1 階層下にあり、ダッシュボードから辿れない。
        稼働テナントで 81 人中 1 人しか設定されていなかったため、未設定の人にだけ
        入口を出す。閉じたら以後は出さない。
      */}
      {reminderUnset && !reminderPromptDismissed && (
        <div className="dashboard-reminder-prompt" role="status" data-testid="reminder-prompt">
          <div className="dashboard-reminder-prompt-body">
            <p className="dashboard-reminder-prompt-text">{t('dashboard.reminderPrompt.text')}</p>
            <Link to="/dashboard/settings/notifications" className="dashboard-reminder-prompt-link">
              {t('dashboard.reminderPrompt.cta')}
            </Link>
          </div>
          <button
            type="button"
            className="dashboard-reminder-prompt-dismiss"
            onClick={dismissReminderPrompt}
            aria-label={t('common.close')}
          >
            ×
          </button>
        </div>
      )}

      {showAnalyzing && (
        <div className="dashboard-analyzing" role="status" aria-live="polite">
          {/* 長引いたらスピナーを止める。回り続ける表示は「壊れている」に見える */}
          {!summaryWait.tooLong && <span className="dashboard-analyzing-spinner" aria-hidden />}
          <span className="dashboard-analyzing-text">
            {t(summaryWait.message.key, summaryWait.message.params)}
          </span>
          <Link to="/dashboard/score" className="dashboard-analyzing-link" data-testid="show-result">
            {t('dashboard.summaryWait.showResult')}
          </Link>
        </div>
      )}

      {(reportError || mcsError) && (
        <div className="dashboard-error" role="alert">
          <span>
            {reportError && mcsError ? (reportError || mcsError) : t('dashboard.errorPartial')}
          </span>
          <button type="button" className="dashboard-error-retry" onClick={() => setRetryKey((k) => k + 1)}>
            {t('dashboard.retry')}
          </button>
        </div>
      )}

      {recordsError && !reportError && !mcsError && (
        <div className="dashboard-records-warning" role="status">
          <span>{recordsError}</span>
          <button type="button" className="dashboard-error-retry" onClick={() => setRetryKey((k) => k + 1)}>
            {t('dashboard.retry')}
          </button>
        </div>
      )}

      {/* 今日のまとめの中身。前回の結果のときは、まとめて分かる形にする（#174） */}
      <div className={showingPreviousResult ? 'dashboard-summary dashboard-summary-previous' : 'dashboard-summary'}>
      {showingPreviousResult && (
        <p className="previous-result-note" data-testid="previous-result-note">
          {report?.lastRecordedAt
            ? t('dashboard.summaryWait.previousNote', {
                time: new Date(report.lastRecordedAt).toLocaleTimeString('ja-JP', {
                  hour: '2-digit',
                  minute: '2-digit',
                }),
              })
            : t('dashboard.summaryWait.previousNoteNoTime')}
        </p>
      )}

      {/*
        v1.1 DCS デイリーコンディション（2026-07-02 確定仕様）
        - condition_display はバンドの表示専用フィールド (label + tone)。hold 時は null で BE 側から返る
        - mcs_mode === 'hold' の間は「データ収集中」バナーのみ、バッジ・comment は非表示
        - early/short_reference は参考値注記付き
        - 数値 (score/p値/バンドコード名) は一切表示しない (D6 堅持)
      */}
      {condition && condition.mcs_mode && (
        <section
          className="dashboard-section daily-condition-section"
          aria-labelledby="daily-condition-heading"
        >
          <h2 id="daily-condition-heading" className="daily-condition-heading">
            {t('dashboard.dailyCondition.title')}
          </h2>

          {condition.mcs_mode === 'hold' ? (
            <div className="daily-condition-hold-banner" role="status">
              <span className="daily-condition-hold-icon" aria-hidden="true">
                📊
              </span>
              <div>
                <p className="daily-condition-hold-title">
                  {t('dashboard.dailyCondition.holdTitle')}
                </p>
                <p className="daily-condition-hold-hint">
                  {t('dashboard.dailyCondition.holdHint')}
                </p>
              </div>
            </div>
          ) : (
            <div className="daily-condition-card">
              {SHOW_CONDITION_BAND && condition.condition_display && (
                <div className="daily-condition-badge-row">
                  <span
                    className={`daily-condition-badge daily-condition-tone-${condition.condition_display.tone}`}
                  >
                    <span
                      className={`daily-condition-badge-dot daily-condition-dot-${condition.condition_display.tone}`}
                      aria-hidden="true"
                    />
                    <span>{condition.condition_display.label}</span>
                  </span>
                  <span className="daily-condition-period">
                    {t('dashboard.dailyCondition.periodChip', {
                      days: condition.fatigue_baseline_days || 28,
                    })}
                  </span>
                </div>
              )}

              {condition.comment && (
                <p className="daily-condition-comment">{condition.comment}</p>
              )}

              {(condition.mcs_mode === 'early_reference' ||
                condition.mcs_mode === 'short_reference') && (
                <p className="daily-condition-mode-note" role="note">
                  {t(`dashboard.dailyCondition.modeNote.${condition.mcs_mode}`)}
                </p>
              )}

              <div className="daily-condition-period-footer">
                <span aria-hidden="true">📊</span>
                {t('dashboard.dailyCondition.periodFooter')}
              </div>
            </div>
          )}

          {/* 疲労度カード (Phase 2+ タグ付き)。hold 時も参考として表示可能だが、値の意味が薄いので非表示に */}
          {condition.mcs_mode !== 'hold' && (
            <div className="daily-condition-fatigue-card">
              <div className="daily-condition-fatigue-header">
                <span className="daily-condition-fatigue-eyebrow">
                  {t('dashboard.dailyCondition.fatigueTitle')}
                  <span className="daily-condition-phase2-tag">Phase 2+</span>
                </span>
              </div>
              <div className="daily-condition-fatigue-row">
                <span
                  className={`daily-condition-fatigue-zone-label daily-condition-fzone-${condition.fatigue_zone}`}
                >
                  {t(`dashboard.dailyCondition.fatigueZone.${condition.fatigue_zone}`)}
                </span>
                <span
                  className={`daily-condition-trend-badge daily-condition-trend-${condition.fatigue_trend}`}
                >
                  {t(`dashboard.dailyCondition.fatigueTrend.${condition.fatigue_trend}`)}
                </span>
              </div>
              <div className="daily-condition-fatigue-bar-wrap" aria-hidden="true">
                <div
                  className={`daily-condition-fatigue-bar daily-condition-fatigue-bar-${condition.fatigue_zone}`}
                  style={{
                    width: `${Math.max(0, Math.min(100, condition.fatigue_score))}%`,
                  }}
                />
              </div>
              <p className="daily-condition-fatigue-hint">
                {t(`dashboard.dailyCondition.fatigueHint.${condition.fatigue_zone}`)}
              </p>
            </div>
          )}

          {condition.voice_gap_message && (
            <div className="daily-condition-voice-gap" role="note">
              <span aria-hidden="true">🎙️</span>
              <span>{condition.voice_gap_message}</span>
            </div>
          )}

          {/* 相談先の案内の画面へ（課題 #101-12。以前は「通知」の画面に進み、相談先が出てこなかった） */}
          <Link to="/dashboard/consult" className="daily-condition-consult-card">
            <span className="daily-condition-consult-icon" aria-hidden="true">
              💬
            </span>
            <div className="daily-condition-consult-text">
              <strong>{t('dashboard.dailyCondition.consultTitle')}</strong>
              <span>{t('dashboard.dailyCondition.consultHint')}</span>
            </div>
            <span className="daily-condition-consult-arrow" aria-hidden="true">
              ›
            </span>
          </Link>
        </section>
      )}

      {/* Pattern A 通知（id=99 は相談導線） */}
      {patternA && patternA.text && (
        <div
          className={`dashboard-pattern-a${patternA.id === 99 ? ' dashboard-pattern-a-counsel' : ''}`}
          role="status"
          aria-live="polite"
        >
          <p>{patternA.text}</p>
          {patternA.id === 99 && (
            <p className="dashboard-pattern-a-counsel-link">
              <Link to="/dashboard/settings">{t('dashboard.patternA.counselingLink')}</Link>
            </p>
          )}
        </div>
      )}

      {/* === 新スキーマ表示 === */}
      {isNew && showDomains && domains && (
        <section className="dashboard-section dashboard-domains" aria-labelledby="dashboard-domains-heading">
          <h2 id="dashboard-domains-heading">{t('dashboard.domains.title')}{previousBadge}</h2>
          <div className="dashboard-domains-grid">
            <div className="dashboard-domain-card dashboard-domain-card-vitality">
              <span className="dashboard-domain-label">{t('dashboard.domains.vitality')}</span>
              <span className="dashboard-domain-value">{Math.round(domains.vitality)}</span>
            </div>
            <div className="dashboard-domain-card dashboard-domain-card-tension">
              <span className="dashboard-domain-label">{t('dashboard.domains.tension')}</span>
              <span className="dashboard-domain-value">{Math.round(domains.tension)}</span>
            </div>
            <div className="dashboard-domain-card dashboard-domain-card-cognition">
              <span className="dashboard-domain-label">{t('dashboard.domains.cognition')}</span>
              <span className="dashboard-domain-value">{Math.round(domains.cognition)}</span>
              <span className="dashboard-domain-meta">
                {t('dashboard.domains.cognitionModeLabel')}: {domains.cognition_mode}
              </span>
            </div>
            <div className="dashboard-domain-card dashboard-domain-card-emotion">
              <span className="dashboard-domain-label">{t('dashboard.domains.emotionStability')}</span>
              <span className="dashboard-domain-value">{Math.round(domains.emotion_stability)}</span>
            </div>
          </div>
        </section>
      )}

      {isNew && showCalm && calm && (
        <section className="dashboard-section dashboard-calm" aria-labelledby="dashboard-calm-heading">
          <h2 id="dashboard-calm-heading">{t('dashboard.calm.title')}{previousBadge}</h2>
          <div className={`dashboard-calm-card dashboard-calm-card-${calm.label}`}>
            <span className="dashboard-calm-level">
              {t(`dashboard.calm.level${calm.level}`)}
            </span>
            <span className="dashboard-calm-avg">
              {t('dashboard.calm.atmosphereAvg', { value: Math.round(calm.atmosphere_avg) })}
            </span>
          </div>
        </section>
      )}

      {isNew && showDayOverDay && dayOverDay && (
        <section className="dashboard-section dashboard-day-over-day" aria-labelledby="dashboard-dod-heading">
          <h2 id="dashboard-dod-heading">{t('dashboard.dayOverDay.title')}{previousBadge}</h2>
          <p className="dashboard-day-over-day-line">
            <span className={`dashboard-day-over-day-direction direction-${dayOverDay.direction ?? 'unknown'}`}>
              {directionLabel(dayOverDay, t)}
            </span>
            {dayOverDay.direction != null && typeof dayOverDay.delta_pct === 'number' && (
              <span className="dashboard-day-over-day-pct">
                {dayOverDay.delta_pct >= 0 ? '+' : ''}
                {dayOverDay.delta_pct.toFixed(1)}%
              </span>
            )}
          </p>
        </section>
      )}

      {isNew && coldStart && coldStart.show_monthly_mcs === false && (
        <p className="dashboard-cold-start-hint" role="note">
          {/*
            日数は出さない（課題 #61 の確定版文言）。参考値の表示は録音した日数ではなく
            解析できた音声の長さで決まるため、日数を出すと基準を取り違えさせる。
          */}
          {t('dashboard.coldStart.notice')}
        </p>
      )}

      {/* === 旧スキーマ（後方互換）表示 === */}
      {isLegacy && (
        <>
          <section className="dashboard-section dashboard-condition">
            <h2>{t('employee.condition')}</h2>
            {conditionEmoji ? (
              <p className="condition-emoji" aria-label={t('employee.condition')}>
                {conditionEmoji}
              </p>
            ) : hasTodayRecord ? (
              <p className="condition-recorded-today" role="status">
                {t('dashboard.recordedToday')}
              </p>
            ) : mcsAvailable ? (
              <p className="condition-placeholder">{t('dashboard.noConditionToday')}</p>
            ) : (
              <p className="condition-empty">{t('dashboard.noDataYet')}</p>
            )}
          </section>
        </>
      )}

      {/*
        振り返りと Q&A を、以前の「今日の結果をくわしく見る」の画面に行かずに読めるようにする（#174。
        2026-11-02 高尾さん決定）。4 つの指標・前日比は上に出ているので、二重に出さない
      */}
      <section className="dashboard-section dashboard-report">
        <h2>{t('dashboard.todayReport')}{previousBadge}</h2>
        {report?.comment ? (
          <div className="report-block">
            <p className="report-comment">{report.comment}</p>
            {isLegacy && reportTrends.length > 0 && (
              <ul className="report-trends">
                {reportTrends.map((tr, i) => (
                  <li key={i}>
                    {tr.label} {tr.direction}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : hasTodayRecord ? (
          /* 当日の録音を全件表示する（課題 #42）。1 件目だけを出していた旧実装では、
             同日に複数回録音した 2 件目以降の解析結果が確認できなかった */
          <div className="report-block report-recorded-fallback">
            <p className="report-today-count">
              {t('dashboard.todayRecordCount', { count: todayEntries.length })}
            </p>
            <ol className="report-today-list">
              {todayEntries.map((entry, i) => (
                <li key={entry.id ?? i} className="report-today-item">
                  <span className="report-today-time">
                    {entry.recordedAt
                      ? new Date(entry.recordedAt).toLocaleTimeString('ja-JP', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : t('dashboard.todayRecordNoTime', { index: i + 1 })}
                  </span>
                  {entry.text ? (
                    <span className="report-comment">{entry.text}</span>
                  ) : entry.analyzed ? (
                    /* 解析は済んでいるが要約文が無い（無音の録音など）。「解析中」とは出さない */
                    <span className="report-today-done">{t('dashboard.todayRecordDone')}</span>
                  ) : (
                    <span className="report-today-pending">
                      {t('dashboard.todayRecordAnalyzing')}
                    </span>
                  )}
                </li>
              ))}
            </ol>
            {!hasTodaySummaryText && (
              <p className="report-empty">{t('dashboard.recordedWaitingReport')}</p>
            )}
          </div>
        ) : (
          <p className="report-empty">{t('dashboard.noReportYet')}</p>
        )}
      </section>

      <section className="dashboard-section dashboard-qa" data-testid="dashboard-qa">
        <h2>{t('dashboard.qaTitle')}</h2>
        <p className="score-intro">{t('dashboard.qaIntro')}</p>
      </section>
      </div>

      <section className="dashboard-section dashboard-mcs">
        <h2>{t('dashboard.monthlyMcs.title')}</h2>
        {mcsShowScore && mcsScore != null ? (
          <div className="mcs-block">
            {/*
              「高リスク／低リスク」とその色分けは、判定が確定したように読めるおそれがあるため、
              言い方を見直すまで従業員の画面には出さない（課題 #101-9。2026-10-07 高尾さん依頼）。
              産業保健職の画面の表示はそのまま
            */}
            <span className="mcs-value" data-testid="mcs-value">
              {mcsScore.toFixed(3)}
            </span>
            <span className="mcs-unit">{t('dashboard.monthlyMcs.scoreUnit')}</span>
            {mcsIsReference && (
              <span
                className="mcs-reference-badge"
                role="status"
                aria-label={t('dashboard.monthlyMcs.referenceBadge')}
              >
                {t('dashboard.monthlyMcs.referenceBadge')}
              </span>
            )}
            <div className="mcs-meta">
              {mcsYearMonth && (
                <span className="mcs-meta-item">{t('dashboard.monthlyMcs.month', { value: mcsYearMonth })}</span>
              )}
              {mcsDataVolumePct != null && (
                <span className="mcs-meta-item">
                  {t('dashboard.monthlyMcs.dataVolume', { percent: mcsDataVolumePct })}
                </span>
              )}
              {mcsCutoffVersion && (
                <span className="mcs-meta-item">
                  {t(`dashboard.monthlyMcs.cutoff.${mcsCutoffVersion}`, {
                    defaultValue: mcsCutoffVersion,
                  })}
                </span>
              )}
            </div>
            {mcsIsReference && (
              <p className="mcs-reference-hint" role="note">
                {t('dashboard.monthlyMcs.referenceHint')}
              </p>
            )}
            {mcs?.monthly_comment && (
              <p className="mcs-message">{mcs.monthly_comment}</p>
            )}
            {mcs?.remarks && (
              <p className="mcs-remarks" role="note">
                {mcs.remarks}
              </p>
            )}
          </div>
        ) : (
          <div className="mcs-countdown-block">
            <p className="mcs-countdown-line">
              {t(mcsCountdown.key, mcsCountdown.params)}
            </p>
            <p className="mcs-countdown-hint">
              {t('dashboard.monthlyMcs.countdownHint')}
            </p>
          </div>
        )}
      </section>

      <LastWeekReportCard />

      <nav className="dashboard-actions">
        <Link to="/dashboard/history" className="dashboard-link">
          {t('employee.history')}
        </Link>
      </nav>
    </div>
  );
}
